import { makeAutoObservable, observable, action } from "mobx";
import { request } from "api/cuisineTypes/api";
import { signUpRequest } from "api/userSignUp/api";
import { errorHandler } from "utils/middlewares/errorHandler";

class OnboardingStore {
  delivery_address = "";
  unit_suite = "";
  selectedMealArray = {};
  selectedCuisines = {};
  isNotSureSelected = false;
  selectedCuisinesOnFe = [];
  error = false;
  errorMessage = "";
  isAlreadyRegistered = false;
  default_date = "";
  meal_allowance_type = "daily";
  meal_allowance_value = "";
  enable_meal_delivery = "";
  enable_overpayment = true;
  recieving_days = [];
  recieving_dates = [];
  isSuccessStep = false;
  cuisineTypes = [];
  dietaryPreferences = [];
  expected_bag = "";
  mealPlanSubsidy = "";
  subsidyErrors = {};
  dateError = false;
  dateErrorMessage = "";

  utm = "";
  utmParams = {};

  dailySubsidyOverride = {
    monday: "",
    tuesday: "",
    wednesday: "",
    thursday: "",
    friday: "",
    saturday: "",
    sunday: "",
  };

  user = {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  };

  location = {
    name: "",
    suite: "",
    street_number: "",
    street_name: "",
    city: "",
    province: "",
    postal_code: "",
    delivery_instructions: "",
    contact_number: "",
    disable_credit_cards: "",
    meal_serieses_attributes: [],
  };

  constructor() {
    makeAutoObservable(this, {
      expected_bag: observable,
      user: observable,
      location: observable,
      recieving_dates: observable,
      enable_meal_delivery: observable,
      recieving_days: observable,
      dailySubsidyOverride: observable,
      default_date: observable,
      meal_allowance_type: observable,
      subsidyErrors: observable,

      handleExpectedBagData: action,
      handleMealSeriesData: action,
      handleMealSeriesAndSubsidiesData: action,
      handlePreferencesData: action,
      handleUserDetailsData: action,
      setSkippedStep: action,
      handleReset: action,
      generateDestinationCode: action,
      validateSubsidyFields: action,
    });
    this.captureUTMFromURL();
  }

  async fetchCuisineTypesData() {
    try {
      const response = await request("GET");

      const cuisineArray = response.data.cuisines;
      const preferencesArray = response.data.dietary_preferences;

      this.cuisineTypes = cuisineArray;
      this.dietaryPreferences = preferencesArray;
    } catch (error) {
      throw error;
    }
  }

  setDailySubsidyOverride(subsidyObj) {
    this.dailySubsidyOverride = { ...this.dailySubsidyOverride, ...subsidyObj };
  }

  isDailySubsidyValid() {
    return this.recieving_days?.every((day) => {
      const value = this.dailySubsidyOverride?.[day];
      return value !== "" && value !== null && value !== undefined;
    });
  }

  isWeeklySubsidyValid() {
    return (
      this.meal_allowance_value !== "" &&
      this.meal_allowance_value !== null &&
      Number(this.meal_allowance_value) > 0
    );
  }

  captureUTMFromURL() {
    if (typeof window === "undefined") return;

    const allowList = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
      "gclid",
      "fbclid",
      "msclkid",
    ];

    const qs = new URLSearchParams(window.location.search);
    const picked = {};

    allowList.forEach((k) => {
      const v = qs.get(k);
      if (v) picked[k] = v;
    });

    if (Object.keys(picked).length === 0) {
      try {
        const cached = localStorage.getItem("utm_params");
        if (cached) Object.assign(picked, JSON.parse(cached));
      } catch (_) {}
    }

    const parts = allowList
      .filter((k) => picked[k] != null)
      .map((k) => `${k}=${encodeURIComponent(picked[k])}`);

    this.utm = parts.length ? parts.join("&") : "";
    this.utmParams = picked;

    try {
      localStorage.setItem("utm_params", JSON.stringify(picked));
    } catch (_) {}
  }

  async dispatchSignUpData() {
    try {
      this.location.meal_serieses_attributes.forEach((mealSeries) => {
        mealSeries.dates_to_add = mealSeries.dates_to_add.map((dateStr) => {
          const [day, month, year] = dateStr.split("-");
          return `${year}-${month}-${day}`;
        });
      });

      const payload = {
        user: this.user,
        location: this.location,
        utm: this.utm,
      };
      this.isSuccessStep = true;

      const response = await signUpRequest("POST", payload);
      this.isAlreadyRegistered = response?.new_user;

      return response;
    } catch (error) {
      errorHandler(error.response);

      return error;
    }
  }

  calculateExpectedBags(value) {
    if (value == "<20") {
      return 20;
    } else if (value == "20-39") {
      return 20;
    } else if (value == "40-59") {
      return 40;
    } else if (value == "60-79") {
      return 60;
    } else if (value == "80-99") {
      return 80;
    } else {
      return 100;
    }
  }

  formatTime(date) {
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }

  calculateNextMondayDate() {
    const today = new Date();
    const daysUntilMonday = 1 + ((1 - today.getDay() + 6) % 7);
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() + daysUntilMonday);

    nextMonday.setHours(0, 0, 0, 0);

    const formattedDate = `${nextMonday.getDate()}-${
      nextMonday.getMonth() + 1
    }-${nextMonday.getFullYear()}`;

    return formattedDate;
  }

  handleExpectedBagData(expectedBags) {
    this.expected_bag = this.calculateExpectedBags(expectedBags);
  }

  handleMealSeriesData(selectedMeals) {
    this.selectedMealArray = selectedMeals;

    const [hoursMinutes, amPm] = (selectedMeals?.target_time ?? "").split(" ");
    const [hours, minutes] = (hoursMinutes ?? "").split(":");

    const targetTime = new Date();
    targetTime?.setHours(
      parseInt(hours, 10) +
        ((amPm?.toLowerCase() === "pm" && hours !== "12") ||
        (hours === "12" && amPm?.toLowerCase() === "am")
          ? 12
          : 0)
    );
    targetTime?.setMinutes(parseInt(minutes, 10));

    const cutoffTime = new Date(targetTime?.getTime() - 2 * 60 * 60 * 1000);
    let cutoffTimeString = this.formatTime(cutoffTime);

    const [cutOffHour, cutOffAmPm] = cutoffTimeString?.split(":");

    if (cutOffHour == "00") {
      let cutOffHours = "12";
      cutoffTimeString = cutOffHours + ":" + cutOffAmPm;
    }

    this.location.meal_serieses_attributes = [
      {
        name: selectedMeals.meal_name,
        target_time: selectedMeals.target_time,
        cutoff: cutoffTimeString,
        expected_bags: this.expected_bag,
        update_expected_bags_automatically: true,
        start_date: "",
        days: [],
        dates_to_add: [],
        subsidy: "",
        subsidy_type: "",
        daily_subsidy: {
          monday: "",
          tuesday: "",
          wednesday: "",
          thursday: "",
          friday: "",
          saturday: "",
          sunday: "",
        },
        meal_plan_preference_attributes: {
          cuisines:
            this.location.meal_serieses_attributes[0]
              ?.meal_plan_preference_attributes.cuisines ?? {},
          dietary_preferences:
            this.location.meal_serieses_attributes[0]
              ?.meal_plan_preference_attributes.dietary_preferences ?? "",
          additional_notes:
            this.location.meal_serieses_attributes[0]
              ?.meal_plan_preference_attributes.additional_notes ?? "",
        },
      },
    ];
  }

  handleMealSeriesAndSubsidiesData(
    days,
    start_date,
    dates_to_add,
    default_subsidy_type,
    default_subsidy,
    disable_credit_cards,
    meal_delivery_option,
    meal_plan_subsidy
  ) {
    this.isNotSureSelected = start_date == "I am not sure yet" ? true : false;
    this.default_date = start_date ? this.formatDate(start_date) : "";
    this.meal_allowance_type = default_subsidy_type;
    this.meal_allowance_value = default_subsidy;
    this.enable_meal_delivery = meal_delivery_option;
    this.enable_overpayment = disable_credit_cards;
    this.recieving_days = days;
    this.meal_plan_subsidy = meal_plan_subsidy;

    const currentDate = new Date();
    const currentDateString = `${currentDate.getDate()}-${
      currentDate.getMonth() + 1
    }-${currentDate.getFullYear()}`;

    const filteredArray = dates_to_add?.map((dateObj) => {
      if (dateObj) {
        for (const key in dateObj) {
          const dates = dateObj[key];
          if (Array.isArray(dates) && dates.includes(currentDateString)) {
            dateObj[key] = dates.filter((date) => date !== currentDateString);
          }
        }
      }
      return dateObj;
    });

    this.recieving_dates = filteredArray;
    this.location.disable_credit_cards = !disable_credit_cards;

    const subsidy_attributes =
      meal_delivery_option !== "every week"
        ? { standard_subsidy: meal_plan_subsidy }
        : {
            subsidy: default_subsidy_type === "daily" ? 0 : default_subsidy,
            subsidy_type: default_subsidy_type,
            daily_subsidy:
              default_subsidy_type == "daily"
                ? {
                    monday: days.includes("monday")
                      ? this.dailySubsidyOverride.monday
                      : 0,
                    tuesday: days.includes("tuesday")
                      ? this.dailySubsidyOverride.tuesday
                      : 0,
                    wednesday: days.includes("wednesday")
                      ? this.dailySubsidyOverride.wednesday
                      : 0,
                    thursday: days.includes("thursday")
                      ? this.dailySubsidyOverride.thursday
                      : 0,
                    friday: days.includes("friday")
                      ? this.dailySubsidyOverride.friday
                      : 0,
                    saturday: days.includes("saturday")
                      ? this.dailySubsidyOverride.saturday
                      : 0,
                    sunday: days.includes("sunday")
                      ? this.dailySubsidyOverride.sunday
                      : 0,
                  }
                : {
                    monday: "0",
                    tuesday: "0",
                    wednesday: "0",
                    thursday: "0",
                    friday: "0",
                    saturday: "0",
                    sunday: "0",
                  },
          };

    const mealSeriesAttributes = {
      start_date:
        start_date === "I am not sure yet"
          ? this.calculateNextMondayDate()
          : start_date?.toString(),

      days: days,
      dates_to_add: dates_to_add?.flatMap((month) => {
        if (month) {
          const keys = Object.keys(month);
          if (keys.length > 0) {
            return month[keys[0]];
          }
        }
        return [];
      }),

      employee_pays_by_credit_card: disable_credit_cards,
      ...subsidy_attributes,
    };

    if (
      this.location.meal_serieses_attributes &&
      this.location.meal_serieses_attributes.length > 0
    ) {
      Object.assign(
        this.location.meal_serieses_attributes[0],
        mealSeriesAttributes
      );
    }
  }

  handlePreferencesData(
    onboarding_cuisines,
    onboarding_dietary_preferences,
    additional_notes,
    formatted_cuisines
  ) {
    this.selectedCuisines = onboarding_cuisines;
    const mealSeries = this.location.meal_serieses_attributes[0];
    if (mealSeries && mealSeries.meal_plan_preference_attributes) {
      mealSeries.meal_plan_preference_attributes.cuisines = formatted_cuisines;

      const dietaryPreferences = onboarding_dietary_preferences
        ?.map((item) => item.trim())
        ?.filter((item) => item)
        ?.join(", ");

      mealSeries.meal_plan_preference_attributes.dietary_preferences =
        dietaryPreferences;
      mealSeries.meal_plan_preference_attributes.additional_notes =
        additional_notes;
    }
  }

  handleUserDetailsData(userDetails) {
    this.user.first_name = userDetails.first_name;
    this.user.last_name = userDetails.last_name;
    this.user.email = userDetails.email;
    this.user.phone = userDetails.phone_number;

    this.delivery_address = userDetails.delivery_address;
    this.unit_suite = userDetails.unit_suite;

    this.location.name = userDetails.company_name;
    this.location.addresses_attributes = [
      {
        suite: userDetails.unit_suite,
        street_number: userDetails.street_number,
        street_name: userDetails.street_name,
        city: userDetails.city,
        province: userDetails.province,
        postal_code: userDetails.postal_code,
        destination_code: this.generateDestinationCode(),
        nickname: "primary",
        delivery_instructions: userDetails.comment,
        delivery_contact_phone: userDetails.phone_number,
        delivery_contact_name: `${userDetails.first_name} ${userDetails.last_name}`,
      },
    ];

    this.location.suite = userDetails.unit_suite;
    this.location.street_number = userDetails.street_number;
    this.location.street_name = userDetails.street_name;
    this.location.city = userDetails.city;
    this.location.province = userDetails.province;
    this.location.postal_code = userDetails.postal_code;

    this.location.delivery_instructions = userDetails.comment;
    this.location.contact_number = userDetails.phone_number;

    this.location.delivery_contact_phone = userDetails.phone_number;
    this.location.delivery_contact_name = `${userDetails.first_name} ${userDetails.last_name}`;
  }

  setSkippedStep(isSkipped) {
    if (isSkipped) {
      this.selectedCuisinesOnFe = [];
      this.location.meal_serieses_attributes[0].meal_plan_preference_attributes.cuisines =
        {};
      this.location.meal_serieses_attributes[0].meal_plan_preference_attributes.dietary_preferences =
        "";
      this.location.meal_serieses_attributes[0].meal_plan_preference_attributes.additional_notes =
        "";
    }
  }

  handleReset() {
    this.selectedMealArray = {};
    this.default_date = "";
    this.meal_allowance_type = "daily";
    this.meal_allowance_value = "";
    this.enable_meal_delivery = "";
    this.enable_overpayment = true;
    this.recieving_days = [];
    this.recieving_dates = [];
    this.isSuccessStep = false;
    this.cuisineTypes = [];
    this.dietaryPreferences = [];
    this.expected_bag = "";

    this.user.first_name = "";
    this.user.last_name = "";
    this.user.email = "";
    this.user.phone = "";

    this.location.name = "";
    this.location.suite = "";
    this.location.street_number = "";
    this.location.street_name = "";
    this.location.city = "";
    this.location.province = "";
    this.location.postal_code = "";
    this.location.delivery_instructions = "";
    this.location.contact_number = "";
    this.location.disable_credit_cards = "";
    this.location.meal_serieses_attributes = [];
    this.utm = "";
    this.utmParams = {};

    this.dateError = false;
    this.dateErrorMessage = "";
  }

  setErrorAlert(visible, errorMessage) {
    this.error = visible;
    this.errorMessage = errorMessage;
  }

  setAlert(visible, errorMessage) {
    this.dateError = visible;
    this.dateErrorMessage = errorMessage;
  }

  setSelectedCuisineOnFe(selectedCuisinesOnFe) {
    this.selectedCuisinesOnFe = selectedCuisinesOnFe;
  }

  generateDestinationCode() {
    const length = 5;
    let charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let destCode = "";
    for (var i = 0, n = charset.length; i < length; ++i) {
      destCode += charset.charAt(Math.floor(Math.random() * n));
    }
    return destCode;
  }

  setErrors(errors) {
    this.subsidyErrors = errors;
  }

  validateSubsidyFields() {
    const newErrors = {};

    if (this.meal_allowance_type == "daily") {
      this.recieving_days?.forEach((day) => {
        const value = this.dailySubsidyOverride[day];
        if (value === undefined || value === "") {
          newErrors[day] = true;
        }
      });

      this.subsidyErrors = newErrors;
      return Object.keys(newErrors).length > 0;
    } else return false;
  }
}

const onboardingStore = new OnboardingStore();
export default onboardingStore;
